<template>
  <div class="filters-popover">
    <div class="filter-pl-section">
      <label
        class="filter-pl-title mb-2"
        :for="configSelectId"
      >Dashboard config</label>
      <select
        :id="configSelectId"
        v-model="dashboardConfigModel"
        class="form-control"
        :disabled="loading"
      >
        <option value="">
          Based on Product Lists
        </option>
        <option value="all">
          All configurations
        </option>
        <option
          v-for="config in configs"
          :key="config.id"
          :value="String(config.id)"
        >
          {{ config.display_name }}
        </option>
      </select>
    </div>

    <div class="filter-pl-section">
      <div class="filter-pl-header">
        <span class="filter-pl-title">
          <i class="bi bi-collection" />
          Product Lists
        </span>
        <span class="filter-pl-actions">
          <button
            type="button"
            class="filter-pl-action-btn"
            :disabled="loading"
            @click="$emit('select-all')"
          >All</button>
          <button
            type="button"
            class="filter-pl-action-btn filter-pl-action-btn--danger"
            :disabled="loading"
            @click="$emit('clear-all')"
          >Clear</button>
        </span>
      </div>

      <input
        v-if="productListsLoaded && productLists.length > 0"
        v-model="listSearch"
        type="text"
        class="filter-pl-search"
        placeholder="Search product lists…"
      >

      <div
        class="filter-pl-list"
        :class="{ 'filter-pl-list--loading': loading }"
      >
        <label
          class="filter-pl-item"
          title="Shows reports that are not attached to any product list. Turn it off and select the Dashboard list to see only items explicitly added to Dashboard."
        >
          <input
            type="checkbox"
            :checked="individualItems"
            @change="$emit('update:individualItems', $event.target.checked)"
          >
          <span>
            Individual items
            <span class="filter-pl-hint">(not in lists)</span>
          </span>
        </label>

        <p
          v-if="productListsLoadError"
          class="filter-pl-status"
        >
          Could not load product lists.
        </p>
        <p
          v-else-if="!productListsLoaded"
          class="filter-pl-status"
        >
          Loading…
        </p>
        <template v-else-if="productLists.length > 0">
          <label
            v-for="list in visibleProductLists"
            :key="list.id"
            class="filter-pl-item"
          >
            <input
              type="checkbox"
              :checked="selectedLists.includes(list.id)"
              :disabled="loading"
              @change="$emit('toggle-list', list.id)"
            >
            <span>
              {{ list.name }}
              <span
                v-if="isDashboardList(list)"
                class="filter-pl-hint"
              >(selected reports)</span>
            </span>
          </label>
          <p
            v-if="visibleProductLists.length === 0"
            class="filter-pl-status"
          >
            No product lists match your search.
          </p>
        </template>
        <p
          v-else
          class="filter-pl-status"
        >
          No product lists yet.
        </p>
      </div>

      <a
        href="/product-id-list/form"
        class="filter-pl-new-link"
      >+ New product list</a>
    </div>

    <div class="filter-menu-item">
      <i class="bi bi-shop filter-menu-icon" />
      <select
        v-model="retailerModel"
        class="form-control filter-menu-select"
      >
        <option
          v-for="option in retailerOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <i class="bi bi-chevron-down filter-menu-chevron" />
    </div>

    <div class="filter-menu-item">
      <i class="bi bi-tags filter-menu-icon" />
      <select
        v-model="categoryModel"
        class="form-control filter-menu-select"
      >
        <option
          v-for="option in categoryOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <i class="bi bi-chevron-down filter-menu-chevron" />
    </div>

    <div class="filter-menu-item">
      <i class="bi bi-box-seam filter-menu-icon" />
      <select
        v-model="brandModel"
        class="form-control filter-menu-select"
      >
        <option
          v-for="option in brandOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <i class="bi bi-chevron-down filter-menu-chevron" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardFiltersPopover',

  props: {
    configs: {
      type: Array,
      default: () => [],
    },
    dashboardConfig: {
      type: String,
      default: '',
    },
    productLists: {
      type: Array,
      default: () => [],
    },
    productListsLoaded: {
      type: Boolean,
      default: false,
    },
    productListsLoadError: {
      type: Boolean,
      default: false,
    },
    individualItems: {
      type: Boolean,
      default: true,
    },
    selectedLists: {
      type: Array,
      default: () => [],
    },
    retailer: {
      type: String,
      default: null,
    },
    category: {
      type: String,
      default: null,
    },
    brand: {
      type: String,
      default: null,
    },
    retailerOptions: {
      type: Array,
      default: () => [],
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    brandOptions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      listSearch: '',
      configSelectId: `dashboard-config-filter-${this._uid}`,
    };
  },

  computed: {
    dashboardConfigModel: {
      get() {
        return this.dashboardConfig;
      },
      set(value) {
        this.$emit('update:dashboardConfig', value);
      },
    },

    retailerModel: {
      get() {
        return this.retailer;
      },
      set(value) {
        this.$emit('update:retailer', value);
      },
    },

    categoryModel: {
      get() {
        return this.category;
      },
      set(value) {
        this.$emit('update:category', value);
      },
    },

    brandModel: {
      get() {
        return this.brand;
      },
      set(value) {
        this.$emit('update:brand', value);
      },
    },

    visibleProductLists() {
      if (!this.listSearch) {
        return this.productLists;
      }

      const search = this.listSearch.toUpperCase();

      return this.productLists.filter(list => list.name.toUpperCase().indexOf(search) > -1);
    },
  },

  methods: {
    isDashboardList(list) {
      const name = list && list.name ? String(list.name) : '';

      return name.trim().toLowerCase() === 'dashboard';
    },
  },
};
</script>

<style lang="scss" scoped>
.filters-popover {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  width: 300px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
}

.filter-pl-section {
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.filter-pl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.filter-pl-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #374151;

  i {
    font-size: 17px;
    color: #718096;
  }
}

.filter-pl-actions {
  flex-shrink: 0;
  white-space: nowrap;
  display: flex;
  gap: 8px;
}

.filter-pl-action-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &--danger {
    color: #ef4444;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    text-decoration: none;
  }
}

.filter-pl-search {
  width: 100%;
  height: 32px;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 12px;
  color: #111827;
  margin-bottom: 6px;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #93c5fd;
  }
}

.filter-pl-list {
  max-height: 180px;
  overflow-y: auto;
}

.filter-pl-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 4px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  margin: 0;
  border-radius: 6px;

  &:hover {
    background: #f8fafc;
  }

  input[type='checkbox'] {
    flex-shrink: 0;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }
}

.filter-pl-hint {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
}

.filter-pl-list--loading .filter-pl-item {
  opacity: 0.5;
  cursor: not-allowed;
}

.filter-pl-new-link {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.filter-pl-status {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 4px;
}

.filter-menu-item {
  position: relative;
  display: flex;
  align-items: center;
  height: 45px;
  border: 1px solid #dfe6ef;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);
}

.filter-menu-item + .filter-menu-item {
  margin-top: 10px;
}

.filter-menu-icon {
  position: absolute;
  left: 18px;
  color: #718096;
  font-size: 17px;
  pointer-events: none;
  z-index: 2;
}

.filter-menu-chevron {
  position: absolute;
  right: 18px;
  color: #7b8492;
  font-size: 18px;
  pointer-events: none;
  z-index: 2;
}

.filter-menu-select {
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 12px;
  background: transparent;
  box-shadow: none !important;
  padding: 0 52px 0 72px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}
</style>
